import { Heading, popErrorToast } from '@boltenergy-be/design-system'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useGetServiceContractReportsQuery, useLazyGetServiceContractReportExportQuery } from 'store/queries/bolt-backend/reports'
import { isReportEmpty, mapAgentBreakdownToSalesTableEntry, mapMonthBreakdownToSalesDashboardChartEntry } from './utils.ts'
import DashboardCard from '../components/dashboard-card/DashboardCard.tsx'
import SalesDashboardChart from '../components/sales-dashboard-chart/SalesDashboardChart.tsx'
import { COUNT_TABLE_COLUMNS, SALES_CHART_DEFAULT_PROPS } from './constants.ts'
import NestedDataTable from '../components/nested-data-table/NestedDataTable.tsx'
import styles from '../Dashboard.module.scss'
import { formatAmount } from 'utils/format.ts'
import { ExportServiceContractReportQueryStringParameters } from '@boltenergy-be/shared'
import { downloadFile } from 'utils/files.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'

const ServiceContractReports = () => {
  // Constants
  const today = useMemo(() => dayjs(), [])
  const from = today.subtract(7, 'months').startOf('month')

  // Redux queries
  const { currentData, isFetching } = useGetServiceContractReportsQuery({
    from: from.format('YYYY-MM'),
    to: today.format('YYYY-MM')
  })
  const [triggerExport] = useLazyGetServiceContractReportExportQuery()

  // Datasets
  const allContractsData = useMemo(
    () => mapMonthBreakdownToSalesDashboardChartEntry(currentData?.allContracts),
    [currentData?.allContracts]
  )
  const nettoSalesData = useMemo(() => mapAgentBreakdownToSalesTableEntry(currentData?.nettoSales, 'count'), [currentData?.nettoSales])
  const portfolioInData = useMemo(() => mapAgentBreakdownToSalesTableEntry(currentData?.portfolioIn, 'count'), [currentData?.portfolioIn])
  const portfolioOutData = useMemo(
    () => mapAgentBreakdownToSalesTableEntry(currentData?.portfolioOut, 'count'),
    [currentData?.portfolioOut]
  )

  /**
   * Export report to CSV through endpoint
   * @param {ExportServiceContractReportQueryStringParameters["report"]} report
   * @returns {Promise<void>}
   */
  const handleExport = async (report: ExportServiceContractReportQueryStringParameters['report']) => {
    try {
      const csv = await triggerExport({ report, from: from.format('YYYY-MM'), to: today.format('YYYY-MM') }).unwrap()
      downloadFile(csv, `${report}-${today.format('YYYY-MM-DD')}.csv`, 'text/csv;charset=utf-8;')
    } catch (err) {
      popErrorToast('Something went wrong while exporting the report')
      log({ error: err as Error, identifier: `[${Flow.REPORTS}:handleExport:${report}]` })
    }
  }

  return (
    <>
      <Heading as="h2" variant="h4">
        Serviced contracts overview
      </Heading>

      <DashboardCard
        variant="large"
        title="Netto sales"
        loading={isFetching}
        className={styles['overview-chart']}
        handleExport={async () => await handleExport('nettoSales')}
        disableExport={isReportEmpty(currentData?.nettoSales)}
      >
        <NestedDataTable columns={COUNT_TABLE_COLUMNS} rows={nettoSalesData} />
      </DashboardCard>

      <DashboardCard
        variant="large"
        title="All contracts"
        loading={isFetching}
        className={styles['overview-chart']}
        handleExport={async () => await handleExport('allContracts')}
        disableExport={isReportEmpty(currentData?.allContracts)}
      >
        <SalesDashboardChart label="Close date" {...SALES_CHART_DEFAULT_PROPS} data={allContractsData} />
      </DashboardCard>

      <DashboardCard
        variant="large"
        title="Portfolio-in"
        className={styles['overview-chart']}
        loading={isFetching}
        handleExport={async () => await handleExport('portfolioIn')}
        canExport={false}
      >
        <NestedDataTable columns={COUNT_TABLE_COLUMNS} rows={portfolioInData} />
      </DashboardCard>

      <div className={styles['overview-kpis']}>
        <DashboardCard
          variant="small"
          title="Portfolio"
          description="Contracts actually being delivered"
          canExport={false}
          loading={isFetching}
          disableExport={!currentData?.portfolio}
        >
          <data className={styles['big-number']} value={currentData?.portfolio}>
            {formatAmount(currentData?.portfolio, { fractionDigits: 0 })}
          </data>
        </DashboardCard>

        <DashboardCard
          variant="small"
          title="Ongoing"
          loading={isFetching}
          handleExport={async () => await handleExport('ongoing')}
          disableExport={!currentData?.ongoing}
        >
          <data className={styles['big-number']} value={currentData?.ongoing}>
            {formatAmount(currentData?.ongoing, { fractionDigits: 0 })}
          </data>
        </DashboardCard>

        <DashboardCard
          variant="small"
          title="Cancelled"
          description="By action of customer"
          loading={isFetching}
          handleExport={async () => await handleExport('cancelled')}
          disableExport={!currentData?.cancelled}
        >
          <data className={styles['big-number']} value={currentData?.cancelled}>
            {formatAmount(currentData?.cancelled, { fractionDigits: 0 })}
          </data>
        </DashboardCard>

        <DashboardCard
          variant="small"
          title="Terminated contracts"
          loading={isFetching}
          handleExport={async () => await handleExport('terminated')}
          disableExport={!currentData?.terminated}
        >
          <data className={styles['big-number']} value={currentData?.terminated}>
            {formatAmount(currentData?.terminated, { fractionDigits: 0 })}
          </data>
        </DashboardCard>
      </div>

      <DashboardCard
        variant="large"
        title="Portfolio-out"
        className={styles['overview-panel']}
        loading={isFetching}
        handleExport={async () => await handleExport('portfolioOut')}
        canExport={false}
      >
        <NestedDataTable columns={COUNT_TABLE_COLUMNS} rows={portfolioOutData} />
      </DashboardCard>
    </>
  )
}

export default ServiceContractReports
